<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps({
  checks: { type: Array, default: () => [] },
  visible: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const expanded = ref(true);

const sourceLabel = source =>
  ({
    funesbom: t('SALES_PROPERTY.CERTIFICATES.FUNESBOM'),
    efiteutica: t('SALES_PROPERTY.CERTIFICATES.EFITEUTICA'),
  })[source] || source;
const itemLabel = item => {
  const source = sourceLabel(item.source);
  if (item.status === 'loading')
    return t('SALES_PROPERTY.CERTIFICATES.LOADING', { source });
  if (item.status === 'not_configured')
    return t('SALES_PROPERTY.CERTIFICATES.NOT_CONFIGURED', { source });
  if (item.status === 'success')
    return t('SALES_PROPERTY.CERTIFICATES.FOUND', { source });
  return t('SALES_PROPERTY.CERTIFICATES.UNAVAILABLE', { source });
};
const statusIconClass = item =>
  ({
    loading: 'i-lucide-loader-circle animate-spin text-lais-text-muted',
    success: 'i-lucide-check text-green-600',
    not_configured: 'i-lucide-x text-red-600',
    unavailable: 'i-lucide-x text-red-600',
  })[item.status] || 'i-lucide-x text-red-600';
const panelClass = computed(() =>
  expanded.value ? 'w-[min(360px,calc(100vw-2rem))]' : 'w-14'
);
const toggleLabel = computed(() =>
  expanded.value
    ? t('SALES_PROPERTY.CERTIFICATES.COLLAPSE')
    : t('SALES_PROPERTY.CERTIFICATES.EXPAND')
);
</script>

<template>
  <div class="contents">
    <aside
      v-if="visible"
      aria-live="polite"
      class="fixed right-4 top-24 z-50 overflow-hidden rounded-lais border border-lais-border bg-white shadow-lg transition-[width]"
      :class="panelClass"
      data-testid="property-certificate-feedback"
    >
      <div class="flex items-center gap-2 p-3">
        <button
          :aria-label="toggleLabel"
          class="grid size-8 shrink-0 place-items-center rounded-lais-sm text-lg hover:bg-lais-bg-soft"
          data-testid="property-certificate-feedback-toggle"
          @click="expanded = !expanded"
        >
          <span
            aria-hidden="true"
            :class="
              expanded ? 'i-lucide-chevrons-left' : 'i-lucide-chevrons-right'
            "
          />
        </button>
        <template v-if="expanded">
          <div class="min-w-0 flex-1 space-y-2">
            <div
              v-for="item in checks"
              :key="item.source"
              class="flex items-center gap-2 text-sm text-lais-text-body"
            >
              <span
                aria-hidden="true"
                class="size-5 shrink-0"
                :class="statusIconClass(item)"
              />
              <span>{{ itemLabel(item) }}</span>
            </div>
          </div>
          <button
            :aria-label="t('SALES_PROPERTY.CERTIFICATES.CLOSE')"
            class="grid size-8 shrink-0 place-items-center rounded-lais-sm text-lg hover:bg-lais-bg-soft"
            data-testid="property-certificate-feedback-close"
            @click="emit('close')"
          >
            <span aria-hidden="true" class="i-lucide-x" />
          </button>
        </template>
      </div>
    </aside>
  </div>
</template>
